
import { useState } from 'react';
import { galleryPhotos, galleryCategories } from '../../../mocks/gallery';

interface LightboxState {
  isOpen: boolean;
  photoId: number | null;
}

function GalleryHero() {
  return (
    <section className="relative h-[420px] w-full overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/5d98fde1b5d1fef5cc2ff5719aba3b99.jpg"
          alt="Roseville California Community Photo Gallery"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
      </div>
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="text-center px-6 w-full max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-5">
            <i className="ri-camera-line text-white"></i>
            <span className="text-white text-sm font-semibold tracking-wide uppercase">Community Gallery</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
            Roseville Through<br />Our Lens
          </h1>
          <p className="text-xl text-white/85 max-w-xl mx-auto font-light">
            Explore the beauty, culture, and community spirit of Roseville captured by local photographers
          </p>
        </div>
      </div>
    </section>
  );
}

function Lightbox({
  photo,
  onClose,
  onPrev,
  onNext,
}: {
  photo: typeof galleryPhotos[0];
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="max-w-5xl w-full mx-4 md:mx-8" onClick={(e) => e.stopPropagation()}>
        <div className="relative rounded-xl overflow-hidden bg-gray-900">
          <img src={photo.src} alt={photo.alt} className="w-full max-h-[75vh] object-contain mx-auto" />
        </div>
        <div className="mt-4 flex items-center justify-between text-white">
          <div>
            <h3 className="text-xl font-bold mb-1">{photo.title}</h3>
            <div className="flex items-center gap-4 text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                {photo.location}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-camera-line"></i>
                {photo.photographer}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {photo.date}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <i className="ri-heart-fill text-rose-400"></i>
            <span className="text-sm font-medium">{photo.likes}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GalleryGrid() {
  const [activeCategory, setActiveCategory] = useState('All Photos');
  const [lightbox, setLightbox] = useState<LightboxState>({ isOpen: false, photoId: null });

  const filtered =
    activeCategory === 'All Photos' ? galleryPhotos : galleryPhotos.filter((p) => p.category === activeCategory);

  const openLightbox = (id: number) => setLightbox({ isOpen: true, photoId: id });
  const closeLightbox = () => setLightbox({ isOpen: false, photoId: null });

  const navigateLightbox = (dir: 'prev' | 'next') => {
    const currentIdx = filtered.findIndex((p) => p.id === lightbox.photoId);
    if (currentIdx === -1) return;
    const newIdx = dir === 'prev' ? (currentIdx - 1 + filtered.length) % filtered.length : (currentIdx + 1) % filtered.length;
    setLightbox({ isOpen: true, photoId: filtered[newIdx].id });
  };

  const lightboxPhoto = galleryPhotos.find((p) => p.id === lightbox.photoId);

  return (
    <>
      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-10">
        {galleryCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeCategory === cat
                ? 'bg-teal-600 text-white shadow-md shadow-teal-200'
                : 'bg-white text-gray-600 hover:bg-teal-50 hover:text-teal-700 border border-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-6 mb-10 text-sm text-gray-500">
        <span className="flex items-center gap-1.5">
          <i className="ri-image-line text-teal-500"></i>
          {filtered.length} photos
        </span>
        <span className="w-px h-4 bg-gray-200"></span>
        <span className="flex items-center gap-1.5">
          <i className="ri-heart-line text-rose-400"></i>
          {filtered.reduce((sum, p) => sum + p.likes, 0).toLocaleString()} total likes
        </span>
        <span className="w-px h-4 bg-gray-200"></span>
        <span className="flex items-center gap-1.5">
          <i className="ri-camera-line text-amber-500"></i>
          {new Set(filtered.map((p) => p.photographer)).size} photographers
        </span>
      </div>

      {/* Masonry Grid */}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
        {filtered.map((photo) => (
          <div
            key={photo.id}
            className="break-inside-avoid group cursor-pointer"
            onClick={() => openLightbox(photo.id)}
          >
            <div className="relative rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
              <img
                src={photo.src}
                alt={photo.alt}
                title={`${photo.title} - Roseville California`}
                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {photo.featured && (
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-[11px] font-bold rounded-full flex items-center gap-1">
                    <i className="ri-star-fill text-[10px]"></i>
                    Featured
                  </span>
                </div>
              )}

              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1">
                  <i className="ri-heart-fill text-rose-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">{photo.likes}</span>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h4 className="text-white font-bold text-sm mb-1">{photo.title}</h4>
                <div className="flex items-center gap-3 text-white/80 text-xs">
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {photo.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-camera-line"></i>
                    {photo.photographer}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20">
          <i className="ri-image-line text-5xl text-gray-300 mb-4 block"></i>
          <p className="text-gray-500 text-lg">No photos found in this category yet.</p>
        </div>
      )}

      {/* Lightbox */}
      {lightbox.isOpen && lightboxPhoto && (
        <Lightbox
          photo={lightboxPhoto}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox('prev')}
          onNext={() => navigateLightbox('next')}
        />
      )}
    </>
  );
}

export { GalleryHero };
